import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/Sections";

const URL = "https://finendra.com/careers";
const OG = "https://finendra.com/og/og-default.png";

const roles = [
  {
    title: "Staff Accountant",
    type: "Full-time · Remote",
    body: "Own the monthly cycle for a portfolio of global clients: reconciliations, journal entries, AP and AR runs, and a clean close on a fixed calendar.",
  },
  {
    title: "Senior Accountant",
    type: "Full-time · Remote",
    body: "Review junior work, run complex closes with accruals and multi-entity consolidation, and act as the day-to-day point of contact for client finance leads.",
  },
  {
    title: "Payroll Specialist",
    type: "Full-time · Remote",
    body: "Run multi-jurisdiction and global payroll cycles, maintain benefit and tax setups, and keep filings and journals accurate across client platforms.",
  },
  {
    title: "Controller",
    type: "Full-time · Remote",
    body: "Lead reporting quality across a client pod, design controls and workflows, and advise founders on cash, margin, and forecasting.",
  },
];

const values = [
  {
    h: "Accuracy is the product",
    p: "We would rather take an extra day than hand over a number we cannot defend. Reviews are part of the process, not a sign of distrust.",
  },
  {
    h: "Documented, not heroic",
    p: "Every client has written workflows. Nothing depends on a single person remembering how something works.",
  },
  {
    h: "Remote with real rhythm",
    p: "Distributed team, fixed close calendars, predictable working patterns. No surprise crunches built into the schedule.",
  },
  {
    h: "Grow into scope",
    p: "Clear progression from staff to senior to controller, with training on the tools and industries we serve.",
  },
];

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: "Careers | Join the Finendra Accounting Team" },
      {
        name: "description",
        content:
          "Remote accounting, payroll, and controller roles at Finendra. Work with global clients on documented processes, real review standards, and clear progression.",
      },
      { property: "og:title", content: "Careers at Finendra" },
      {
        property: "og:description",
        content:
          "Remote roles for accountants, payroll specialists, and controllers serving businesses worldwide.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: CareersPage,
});

function CareersPage() {
  return (
    <>
      <PageHero
        eyebrow="Careers"
        title="Build finance operations people can rely on."
        body="We hire accountants who care about clean books and clear communication. Roles below are the areas we hire into consistently — send us your details even if timing is not exact."
      />

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-4xl divide-y divide-brand-navy/10 border-y border-brand-navy/10">
          {roles.map((r, i) => (
            <Reveal
              key={r.title}
              delay={i * 0.05}
              className="flex flex-col gap-4 py-8 md:flex-row md:items-start md:justify-between"
            >
              <div className="max-w-xl">
                <h2 className="font-display text-xl font-bold text-brand-navy">{r.title}</h2>
                <p className="mt-1 text-xs font-bold uppercase tracking-widest text-brand-accent">
                  {r.type}
                </p>
                <p className="mt-3 leading-relaxed text-brand-navy/60">{r.body}</p>
              </div>
              <a
                href={`mailto:connect@finendra.com?subject=${encodeURIComponent(
                  `Application — ${r.title}`,
                )}`}
                className="shrink-0 self-start rounded-full bg-brand-navy px-6 py-3 text-sm font-semibold text-background transition-colors hover:bg-brand-accent"
              >
                Apply
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-brand-surface px-6 py-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <h2 className="font-display text-4xl font-bold text-brand-navy">How we work</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal
                key={v.h}
                delay={i * 0.06}
                className="rounded-3xl border border-brand-navy/10 bg-background p-8"
              >
                <h3 className="font-display text-lg font-bold text-brand-navy">{v.h}</h3>
                <p className="mt-3 leading-relaxed text-brand-navy/60">{v.p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-24">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-brand-navy">No role that fits?</h2>
          <p className="mt-5 text-brand-navy/60">
            Send your CV and a short note about the work you want to do to{" "}
            <a
              href="mailto:connect@finendra.com"
              className="font-semibold text-brand-accent underline underline-offset-4"
            >
              connect@finendra.com
            </a>
            . We keep strong applications on file and reach out when a match opens.
          </p>
        </Reveal>
      </section>
    </>
  );
}
